import { useMutation, useQueryClient } from '@tanstack/react-query';
import { QUERY_KEYS, stanNotify } from '@/lib';
import { addContact, importContactsCSV } from '@/lib/api/customers';
import type { AddContactPayload } from '../types/customers.types';
import { useAuthStore } from '@/stores';

export function useAddContact() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (payload: AddContactPayload) => addContact(payload),
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.AUDIENCE] });
            const contactName = variables.name || variables.email || 'Contact';
            stanNotify({
                type: 'success',
                title: 'Contact added',
                text: `${contactName} was successfully imported`,
                duration: 3000,
            });
        },
        onError: (error: { response?: { data?: { message?: string } } }) => {
            stanNotify({
                type: 'error',
                title: 'Failed to Add Contact',
                text: error.response?.data?.message || 'Failed to add contact',
                duration: 3000,
            });
        },
    });
}

export function useImportContactsCSV() {
    const queryClient = useQueryClient();
    const token = useAuthStore(state => state.token);

    return useMutation({
        mutationFn: ({ file, tag }: { file: File; tag?: string }) => importContactsCSV(file, tag),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.AUDIENCE] });
            // Refetch user data to update running_jobs and show progress banner
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.USER, token] });
            // Show notification about import time
            stanNotify({
                type: 'success',
                title: 'Import Started',
                text: 'Your contacts are being imported.',
                duration: 5000,
            });
        },
        onError: (error: { response?: { data?: { message?: string } } }) => {
            stanNotify({
                type: 'error',
                title: 'Import Failed',
                text: error.response?.data?.message || 'Failed to import contacts',
                duration: 3000,
            });
        },
    });
}
